import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExclamationCircleIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { useCourses } from '../../contexts/CourseContext';

interface CourseFormData {
  courseName: string; // 필수
  maxStudents: number; // 필수
  waitingList: number;
  tuition: number; // 필수
  materialFee: number;
  courseDescription: string; // 필수
  registrationStartDate: string; // 필수
  registrationEndDate: string; // 필수
  registrationStartTime: string;
  registrationEndTime: string;
  courseStartDate: string; // 필수
  courseEndDate: string; // 필수
  courseCycle: string;
  courseDays: string[];
  courseTime: string; // 필수
  totalSessions: number;
  institution: string;
  location: string; // 필수
  contactPerson: string;
  contactPhone: string;
  instructor: string; // 필수
  selectionMethod: 'first-come' | 'lottery'; // 필수
  courseImage?: string; // 강의 이미지
}

const CourseForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addCourse, updateCourse, getCourse } = useCourses();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<CourseFormData>({
    courseName: '',
    maxStudents: 20,
    waitingList: 5,
    tuition: 0,
    materialFee: 0,
    courseDescription: '',
    registrationStartDate: '',
    registrationEndDate: '',
    registrationStartTime: '09:00',
    registrationEndTime: '18:00',
    courseStartDate: '',
    courseEndDate: '',
    courseCycle: 'weekly',
    courseDays: [],
    courseTime: '',
    totalSessions: 0,
    institution: '동두천 글로벌인재센터',
    location: '',
    contactPerson: '',
    contactPhone: '',
    instructor: '',
    selectionMethod: 'first-come',
    courseImage: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CourseFormData, string>>>({});

  const daysOfWeek = ['월', '화', '수', '목', '금', '토', '일'];
  const courseCycles = [
    { value: 'weekly', label: '주간' },
    { value: 'daily', label: '일일' },
    { value: 'monthly', label: '월간' },
    { value: 'intensive', label: '집중' }
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? Number(value) : value
    });
    // Clear error when user starts typing
    if (errors[name as keyof CourseFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleDayToggle = (day: string) => {
    const updatedDays = formData.courseDays.includes(day)
      ? formData.courseDays.filter(d => d !== day)
      : [...formData.courseDays, day];
    setFormData({ ...formData, courseDays: updatedDays });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, courseImage: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof CourseFormData, string>> = {};

    // 필수 필드 검증
    if (!formData.courseName) newErrors.courseName = '강좌명은 필수입니다.';
    if (formData.maxStudents <= 0) newErrors.maxStudents = '접수인원은 1명 이상이어야 합니다.';
    if (formData.tuition < 0) newErrors.tuition = '수강료는 0원 이상이어야 합니다.';
    if (!formData.courseDescription) newErrors.courseDescription = '교육내용은 필수입니다.';
    if (!formData.registrationStartDate) newErrors.registrationStartDate = '접수 시작일은 필수입니다.';
    if (!formData.registrationEndDate) newErrors.registrationEndDate = '접수 종료일은 필수입니다.';
    if (!formData.courseStartDate) newErrors.courseStartDate = '교육 시작일은 필수입니다.';
    if (!formData.courseEndDate) newErrors.courseEndDate = '교육 종료일은 필수입니다.';
    if (!formData.courseTime) newErrors.courseTime = '교육시간은 필수입니다.';
    if (!formData.location) newErrors.location = '교육장소는 필수입니다.';
    if (!formData.instructor) newErrors.instructor = '강사명은 필수입니다.';

    // 날짜 논리 검증
    if (formData.registrationStartDate && formData.registrationEndDate) {
      if (new Date(formData.registrationStartDate) > new Date(formData.registrationEndDate)) {
        newErrors.registrationEndDate = '접수 종료일은 시작일 이후여야 합니다.';
      }
    }
    if (formData.courseStartDate && formData.courseEndDate) {
      if (new Date(formData.courseStartDate) > new Date(formData.courseEndDate)) {
        newErrors.courseEndDate = '교육 종료일은 시작일 이후여야 합니다.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const courseData = {
        ...formData,
        courseCycle: formData.courseCycle as 'weekly' | 'daily' | 'monthly' | 'intensive',
        status: 'recruiting' as const
      };

      if (isEditMode && id) {
        updateCourse(id, courseData);
        alert('강의가 성공적으로 수정되었습니다!');
      } else {
        addCourse(courseData);
        alert('강의가 성공적으로 등록되었습니다!');
      }
      navigate('/admin/courses');
    }
  };

  // 템플릿 데이터 적용
  const applyTemplate = () => {
    const templateData: CourseFormData = {
      courseName: '초급 영어회화 - 일상생활 편',
      maxStudents: 15,
      waitingList: 5,
      tuition: 120000,
      materialFee: 15000,
      courseDescription: '일상생활에서 자주 사용되는 기초 영어회화를 학습합니다. 인사, 쇼핑, 길찾기, 식당 주문 등 실생활에 필요한 표현들을 중심으로 진행됩니다. 초보자도 쉽게 따라올 수 있는 수준으로 구성되었습니다.',
      registrationStartDate: '2024-12-15',
      registrationEndDate: '2024-12-25',
      registrationStartTime: '09:00',
      registrationEndTime: '18:00',
      courseStartDate: '2025-01-02',
      courseEndDate: '2025-03-28',
      courseCycle: 'weekly',
      courseDays: ['화', '목'],
      courseTime: '10:00-12:00',
      totalSessions: 24,
      institution: '동두천 글로벌인재센터',
      location: '2층 201호 강의실',
      contactPerson: '김교육',
      contactPhone: '031-123-4567',
      instructor: '제임스 리',
      selectionMethod: 'first-come' as 'first-come'
    };
    setFormData(templateData);
    alert('템플릿이 적용되었습니다. 필요한 부분을 수정해주세요.');
  };

  // Edit mode: Load existing course data
  useEffect(() => {
    if (isEditMode && id) {
      const course = getCourse(id);
      if (course) {
        setFormData({
          courseName: course.courseName,
          maxStudents: course.maxStudents,
          waitingList: course.waitingList,
          tuition: course.tuition,
          materialFee: course.materialFee,
          courseDescription: course.courseDescription,
          registrationStartDate: course.registrationStartDate,
          registrationEndDate: course.registrationEndDate,
          registrationStartTime: course.registrationStartTime,
          registrationEndTime: course.registrationEndTime,
          courseStartDate: course.courseStartDate,
          courseEndDate: course.courseEndDate,
          courseCycle: course.courseCycle,
          courseDays: course.courseDays,
          courseTime: course.courseTime,
          totalSessions: course.totalSessions,
          institution: course.institution,
          location: course.location,
          contactPerson: course.contactPerson,
          contactPhone: course.contactPhone,
          instructor: course.instructor,
          selectionMethod: course.selectionMethod,
          courseImage: course.courseImage || ''
        });
      }
    }
  }, [isEditMode, id, getCourse]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {isEditMode ? '강의 수정' : '강의 등록'}
            </h1>
            <p className="text-gray-600">
              {isEditMode ? '강의 정보를 수정합니다.' : '새로운 강의를 등록합니다.'}
              <span className="text-red-500">*</span> 표시는 필수 항목입니다.
            </p>
          </div>
          {!isEditMode && (
            <button
              type="button"
              onClick={applyTemplate}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
              템플릿 적용
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
        {/* 기본 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">기본 정보</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                강좌명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="courseName"
                value={formData.courseName}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.courseName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="예: 비즈니스 영어회화"
              />
              {errors.courseName && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.courseName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                강사명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="instructor"
                value={formData.instructor}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.instructor ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="예: 홍길동"
              />
              {errors.instructor && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.instructor}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                강의 이미지
              </label>
              {formData.courseImage && (
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-2">현재 이미지:</p>
                  <img
                    src={formData.courseImage}
                    alt="강의 이미지 미리보기"
                    className="w-full max-w-md h-64 object-cover rounded-lg border border-gray-300"
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.courseImage ? '새 이미지를 업로드하려면 파일을 선택하세요' : '이미지 파일을 선택하세요'}
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육내용 <span className="text-red-500">*</span>
              </label>
              <textarea
                name="courseDescription"
                value={formData.courseDescription}
                onChange={handleInputChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.courseDescription ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="교육 내용을 상세히 입력해주세요..."
              />
              {errors.courseDescription && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.courseDescription}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 모집 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">모집 정보</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                선발 방식 <span className="text-red-500">*</span>
              </label>
              <select
                name="selectionMethod"
                value={formData.selectionMethod}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="first-come">선착순</option>
                <option value="lottery">추첨</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  접수인원 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="maxStudents"
                  value={formData.maxStudents}
                  onChange={handleInputChange}
                  min="1"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.maxStudents ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.maxStudents && (
                  <p className="mt-1 text-sm text-red-500">
                    <ExclamationCircleIcon className="h-4 w-4 inline mr-1" />
                    {errors.maxStudents}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  대기인원
                </label>
                <input
                  type="number"
                  name="waitingList"
                  value={formData.waitingList}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                수강료 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="tuition"
                value={formData.tuition}
                onChange={handleInputChange}
                min="0"
                step="1000"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.tuition ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.tuition && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.tuition}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                재료/교재비
              </label>
              <input
                type="number"
                name="materialFee"
                value={formData.materialFee}
                onChange={handleInputChange}
                min="0"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* 접수 기간 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">접수 기간</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                접수 시작일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="registrationStartDate"
                value={formData.registrationStartDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.registrationStartDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.registrationStartDate && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.registrationStartDate}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                접수 종료일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="registrationEndDate"
                value={formData.registrationEndDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.registrationEndDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.registrationEndDate && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.registrationEndDate}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                접수 시작 시간
              </label>
              <input
                type="time"
                name="registrationStartTime"
                value={formData.registrationStartTime}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                접수 종료 시간
              </label>
              <input
                type="time"
                name="registrationEndTime"
                value={formData.registrationEndTime}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* 교육 기간 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">교육 기간</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육 시작일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="courseStartDate"
                value={formData.courseStartDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.courseStartDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.courseStartDate && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.courseStartDate}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육 종료일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="courseEndDate"
                value={formData.courseEndDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.courseEndDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.courseEndDate && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.courseEndDate}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육주기
              </label>
              <select
                name="courseCycle"
                value={formData.courseCycle}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {courseCycles.map(cycle => (
                  <option key={cycle.value} value={cycle.value}>
                    {cycle.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육시간 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="courseTime"
                value={formData.courseTime}
                onChange={handleInputChange}
                placeholder="예: 10:00-12:00"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.courseTime ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.courseTime && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.courseTime}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육요일
              </label>
              <div className="flex gap-2 flex-wrap">
                {daysOfWeek.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(day)}
                    className={`px-3 py-1 rounded-lg border transition-colors ${
                      formData.courseDays.includes(day)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                총 수강일수
              </label>
              <input
                type="number"
                name="totalSessions"
                value={formData.totalSessions}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* 장소 및 연락처 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">장소 및 연락처</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육기관명
              </label>
              <input
                type="text"
                name="institution"
                value={formData.institution}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                교육장소 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="예: 3층 301호"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.location ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.location && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.location}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                문의담당자
              </label>
              <input
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                문의전화
              </label>
              <input
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                placeholder="예: 031-123-4567"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/courses')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {isEditMode ? '수정 완료' : '강의 등록'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CourseForm;